import type { TeamsMapGrouped } from "../utils/parseTeamsData";
import type { SportsMap } from "../utils/parseSportData";
import type { CompetitionsMap } from "../utils/parseCompetitionsData";
import type { CompetitionTypeMap } from "../utils/parseCompetitionTypeData";

export type CUIBrandType = "czr" | "wh" | "czr-palace" | "horseshoe";

export type CUIConfigType = { 
    imagesDir: string;
    teamsMap?: TeamsMapGrouped;
    sportsMap?: SportsMap;
    competitionsMap?: CompetitionsMap;
    competitionTypeMap?: CompetitionTypeMap;
    brand?: CUIBrandType;
};

declare global {
    interface Window {
      CUI?: CUIConfigType;
    }
    var CUI: CUIConfigType | undefined;
}

const defaultConfig: CUIConfigType = { imagesDir: "/public", brand: "czr" };

/** Gets the global object (window or globalThis) */
function getGlobalObject(): (typeof globalThis & { CUI?: CUIConfigType }) | undefined {
    try {
        if (typeof window !== "undefined") return window;
        if (typeof globalThis !== "undefined") return globalThis;
    } catch {
        // Silently handle restricted environments
    }
    return undefined;
}

/** Ensures CUI object exists on the global scope */
function ensureCUI(): CUIConfigType {
    const g = getGlobalObject();
    if (!g) return { ...defaultConfig };
    if (!g.CUI) g.CUI = { ...defaultConfig };
    return g.CUI;
}

/** Gets the CUI global object (always returns valid config) */
export function getCUI(): CUIConfigType {
    return ensureCUI();
}

/** Gets teamsMap from global CUI */
export function getTeamsMap(): TeamsMapGrouped | undefined {
    try {
        return getCUI().teamsMap;
    } catch {
        return undefined;
    }
}

/** Sets teamsMap on global CUI */
export function setTeamsMap(teamsMap: TeamsMapGrouped): void {
    if (!teamsMap || typeof teamsMap !== "object") return;
    try {
        ensureCUI().teamsMap = teamsMap;
    } catch {
        // Silently fail if global is frozen/restricted
    }
}

/** Checks if teamsMap is loaded and valid */
export function hasTeamsMap(): boolean {
    try {
        const tm = getTeamsMap();
        return tm !== undefined && tm !== null && typeof tm.data === "object" && tm.data !== null;
    } catch {
        return false;
    }
}

/** Gets sportsMap from global CUI */
export function getSportsMap(): SportsMap | undefined {
    try { return getCUI().sportsMap; } catch { return undefined; }
}

/** Sets sportsMap on global CUI */
export function setSportsMap(sportsMap: SportsMap): void {
    if (!sportsMap || typeof sportsMap !== "object") return;
    try { ensureCUI().sportsMap = sportsMap; } catch {}
}

/** Checks if sportsMap is loaded and valid */
export function hasSportsMap(): boolean {
    try { const m = getSportsMap(); return m !== undefined && m !== null && Object.keys(m).length > 0; } catch { return false; }
}

/** Gets competitionsMap from global CUI */
export function getCompetitionsMap(): CompetitionsMap | undefined {
    try { return getCUI().competitionsMap; } catch { return undefined; }
}

/** Sets competitionsMap on global CUI */
export function setCompetitionsMap(competitionsMap: CompetitionsMap): void {
    if (!competitionsMap || typeof competitionsMap !== "object") return;
    try { ensureCUI().competitionsMap = competitionsMap; } catch {}
}

/** Checks if competitionsMap is loaded and valid */
export function hasCompetitionsMap(): boolean {
    try { const m = getCompetitionsMap(); return m !== undefined && m !== null && Object.keys(m).length > 0; } catch { return false; }
}

/** Gets competitionTypeMap from global CUI */
export function getCompetitionTypeMap(): CompetitionTypeMap | undefined {
    try { return getCUI().competitionTypeMap; } catch { return undefined; }
}

/** Sets competitionTypeMap on global CUI */
export function setCompetitionTypeMap(competitionTypeMap: CompetitionTypeMap): void {
    if (!competitionTypeMap || typeof competitionTypeMap !== "object") return;
    try { ensureCUI().competitionTypeMap = competitionTypeMap; } catch {}
}

/** Checks if competitionTypeMap is loaded and valid */
export function hasCompetitionTypeMap(): boolean {
    try { const m = getCompetitionTypeMap(); return m !== undefined && m !== null && Object.keys(m).length > 0; } catch { return false; }
}

/** Gets brand from global CUI, defaults to "czr" */
export function getBrand(): CUIBrandType {
    try {
        return getCUI().brand ?? "czr";
    } catch {
        return "czr";
    }
}

/** Initializes CUI config with provided values */
export const setupCUIConfig = (config?: Partial<CUIConfigType>): void => {
    if (!config || typeof config !== "object") return;
    if (config?.imagesDir !== undefined && typeof config?.imagesDir !== "string") {
        console.error("CUI: imagesDir must be a string");
        return;
    }
    try {
        const cui = ensureCUI();
        Object.assign(cui, config);
    } catch (error) {
        console.error("CUI: Failed to initialize config", error);
    }
};

/** React hook to access CUI config (always returns valid config) */
export const useCuiConfig = (): CUIConfigType => getCUI();

/*
 * LEGACY: localStorage-based config (commented out, kept for reference)
 * 
 * import isEqual from "lodash-es/isEqual";
 * 
 * const hasWindow = typeof window !== "undefined";
 * let prevArgs;
 * const configFromLocalStorage = hasWindow ? window?.localStorage.getItem("cui:config") : "";
 * let config = configFromLocalStorage ? JSON.parse(configFromLocalStorage) : defaultConfig;
 * 
 * export const useCuiConfig = (args?: { imagesDir: string }) => {
 *     if (hasWindow && "CUI" in window && typeof window["CUI"] === "object" && window["CUI"] && "imagesDir" in window["CUI"] && typeof window.CUI.imagesDir === "string") {
 *         return window["CUI"];
 *     }
 *     if (hasWindow && !window?.localStorage) {
 *         console.error("Missing localStorage:CUI uses localStorage to keep configuration.");
 *         return defaultConfig;
 *     }
 *     if (args && "imagesDir" in args && args.imagesDir && !isEqual(prevArgs, args)) {
 *         localStorage.setItem("cui:config", JSON.stringify(args));
 *         config = args;
 *         prevArgs = args;
 *     }
 *     return config;
 * }
 */
